import {
  getCustomerOrganization,
  getOrCreateCustomerOrganizationForNotice
} from '../notice/customer';
import { getModelFromSnapshot } from '../model';
import { safeGetModelArrayFromQuery } from '../model/getModel';
import { CustomerOrganizationModel } from '../model/objects/customerOrganizationModel';
import { UserNoticeModel } from '../model/objects/userNoticeModel';
import { safeAsync } from '../safeWrappers';
import {
  CustomerOrganization,
  EFirebaseContext,
  EOrganization,
  ERef,
  ESnapshotExists
} from '../types';
import {
  ResponseOrColumnError,
  ResponseOrError,
  wrapError,
  wrapSuccess
} from '../types/responses';

class CustomerOrganizationService {
  public constructor(private context: EFirebaseContext) {}

  /**
   * The customer organization for a notice's filing organization at its
   * publisher, as a model, creating it when absent. Returns null for notices
   * filed by an individual (no filing organization).
   */
  public async getOrCreateForNotice(
    notice: UserNoticeModel
  ): Promise<ResponseOrError<CustomerOrganizationModel | null>> {
    const [customerOrgError, customerOrg] = await safeAsync(() =>
      getOrCreateCustomerOrganizationForNotice(this.context, notice)
    )();
    if (customerOrgError) {
      return wrapError(customerOrgError);
    }
    if (!customerOrg) {
      return wrapSuccess(null);
    }
    return wrapSuccess(
      getModelFromSnapshot(CustomerOrganizationModel, this.context, customerOrg)
    );
  }

  /**
   * Every customer organization for a filing organization, across publishers.
   * Used to find an ad system id the same advertiser already has at another
   * paper in a group.
   */
  public async findByClient(
    client: ERef<EOrganization>
  ): Promise<ResponseOrColumnError<CustomerOrganizationModel[]>> {
    return safeGetModelArrayFromQuery(
      CustomerOrganizationModel,
      this.context,
      this.context.customerOrganizationsRef().where('client', '==', client)
    );
  }

  public async maybeGetCustomerOrgFromAdvertiserOrgAndNewspaper(
    advertiserOrg: ERef<EOrganization>,
    newspaperOrg: ERef<EOrganization>
  ): Promise<ResponseOrError<ESnapshotExists<CustomerOrganization> | null>> {
    const [advertiserOrgError, advertiserOrgSnap] = await safeAsync(() =>
      advertiserOrg.get()
    )();
    if (advertiserOrgError) {
      return wrapError(advertiserOrgError);
    }
    const [newspaperError, newspaper] = await safeAsync(() =>
      newspaperOrg.get()
    )();
    if (newspaperError) {
      return wrapError(newspaperError);
    }
    return safeAsync(() =>
      getCustomerOrganization(this.context, advertiserOrgSnap, newspaper)
    )();
  }
}

export default CustomerOrganizationService;
